import {
  DEFAULT_SCHEDULE_STATE,
  RECURRENCE_TYPE,
  SCHEDULE_AM_PM,
  type RecurrenceType,
  type ScheduleAmPm,
} from "@/lib/schedule"

const HOUR_12_MIN = 1
const HOUR_12_MAX = 12
const HOUR_24_MIN = 0
const HOUR_24_MAX = 23
const MINUTE_MIN = 0
const MINUTE_MAX = 59
const DAY_INDEX_MIN = 0
const DAY_INDEX_MAX = 6

export function toScheduleInteger(value: string): number | null {
  const parsed = Number.parseInt(value, 10)
  return Number.isNaN(parsed) ? null : parsed
}

export function isValidScheduleMinute(value: number): boolean {
  return value >= MINUTE_MIN && value <= MINUTE_MAX
}

export function isValidScheduleHour12(value: number): boolean {
  return value >= HOUR_12_MIN && value <= HOUR_12_MAX
}

export function isValidScheduleHour24(value: number): boolean {
  return value >= HOUR_24_MIN && value <= HOUR_24_MAX
}

export function normalizeScheduleAmPm(value: string): ScheduleAmPm {
  return value === SCHEDULE_AM_PM.PM ? SCHEDULE_AM_PM.PM : SCHEDULE_AM_PM.AM
}

export function normalizeScheduleRecurrenceType(value: string): RecurrenceType {
  return value === RECURRENCE_TYPE.WEEKLY
    ? RECURRENCE_TYPE.WEEKLY
    : RECURRENCE_TYPE.DAILY
}

export function normalizeScheduleSelectedDays(
  selectedDays: Set<number>,
): Set<number> {
  return new Set(
    Array.from(selectedDays).filter(
      (day) =>
        Number.isInteger(day) && day >= DAY_INDEX_MIN && day <= DAY_INDEX_MAX,
    ),
  )
}

export function resolveScheduleTime12FromState(
  scheduleHour: string,
  scheduleMinute: string,
  scheduleAmPm: string,
): { hour12: number; minute: number; amPm: ScheduleAmPm } {
  const parsedHour = toScheduleInteger(scheduleHour)
  const parsedMinute = toScheduleInteger(scheduleMinute)
  const hour12 =
    parsedHour !== null && isValidScheduleHour12(parsedHour)
      ? parsedHour
      : (toScheduleInteger(DEFAULT_SCHEDULE_STATE.scheduleHour) ?? HOUR_12_MIN)
  const minute =
    parsedMinute !== null && isValidScheduleMinute(parsedMinute)
      ? parsedMinute
      : (toScheduleInteger(DEFAULT_SCHEDULE_STATE.scheduleMinute) ?? MINUTE_MIN)

  return {
    hour12,
    minute,
    amPm: normalizeScheduleAmPm(scheduleAmPm),
  }
}
